import React, { useCallback, useEffect, useState } from 'react';
import { OrganizationDetailForm } from '../organization-detail-form';
import { OrganizationDetailViewer } from '../organization-detail-view';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Organization,
  OrganizationDetailEditQuery,
  useOrganizationDetailEditQuery,
  useRemoveOrganizationMutation,
} from '../../../../types/generated-types';

import { Typography } from '@mui/material';
import { Notifier } from '../../../system/services/notificationManager';
import {
  BottomMenuItem,
  useInjectableComponents,
} from '../../../system/services/injectableComponentsManager';
import { Remove, Visibility, Edit } from '@mui/icons-material';
import FullWidthLoadingSkeleton from '../../shared/fullWidthLoadingSkeleton';

type MyOrganization = OrganizationDetailEditQuery['organizationById'];

export const OrganizationDetailHandler = () => {
  const { id } = useParams<{ id: string }>();
  const [organization, setOrganization] = useState<MyOrganization>();
  const { data, error, loading } = useOrganizationDetailEditQuery({
    variables: { id: id ?? '' },
  });

  useEffect(() => {
    if (error) {
      Notifier.error(
        'There was a problem fetching the organization information: ',
        error,
      );
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      setOrganization(data.organizationById);
    }
  }, [data]);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [deleteOrganization] = useRemoveOrganizationMutation();
  const [viewMode, setViewMode] = useState(false);

  useEffect(() => {
    setViewMode(pathname?.endsWith('view-organization'));
  }, [pathname]);

  const handleRemoveOrganization = useCallback(async () => {
    try {
      await deleteOrganization({
        variables: {
          id: id ?? '',
        },
      });

      Notifier.success('Organization removed');
      navigate('/organizations');
    } catch (e) {
      Notifier.error('Error removing Organization');
      console.error(e);
    }
  }, [id, deleteOrganization, navigate]);

  const { setContextMenuItems } = useInjectableComponents();

  useEffect(() => {
    if (organization && !error) {
      const items: BottomMenuItem[] = [];

      items.push({
        id: 'remove-organization-menu-item',
        label: 'Remove Organization',
        icon: <Remove fontSize="small" />,
        confirmableAction: {
          title: 'Remove Organization',
          prompt: 'Are you sure you want to remove this organization?',
          actionLabel: 'Proceed',
          cancelLabel: 'Cancel',
          action: handleRemoveOrganization,
        },
      });

      if (!pathname.includes('edit-organization')) {
        items.push({
          id: 'edit-organization-menu-item',
          label: 'Edit Organization Details',
          icon: <Edit fontSize="small" />,
          navTarget: `/organizations/${id}/edit-organization`,
        });
      }

      if (!pathname.includes('view-organization')) {
        items.push({
          id: 'view-organization-menu-item',
          label: 'View Organization Details',
          icon: <Visibility fontSize="small" />,
          navTarget: `/organizations/${id}/view-organization`,
        });
      }

      setContextMenuItems(items.length > 0 ? [{ items }] : undefined);
    } else {
      setContextMenuItems(undefined);
    }
    return () => {
      setContextMenuItems(undefined);
    };
  }, [
    organization,
    error,
    handleRemoveOrganization,
    pathname,
    id,
    setContextMenuItems,
  ]);

  return error ? (
    <div>Unable to access requested organization. {error.message}</div>
  ) : loading ? (
    <FullWidthLoadingSkeleton />
  ) : (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        height: '100%',
        overflowY: 'scroll',
      }}
    >
      <Typography variant="h3" sx={{ fontSize: '1.4rem', margin: '1rem' }}>
        {data?.organizationById
          ? data?.organizationById?.name
          : 'Create Organization'}
      </Typography>
      {viewMode && data?.organizationById ? (
        <OrganizationDetailViewer
          organization={organization as Partial<Organization>}
        />
      ) : data?.organizationById ? (
        <OrganizationDetailForm
          organization={organization as Partial<Organization>}
        />
      ) : (
        <OrganizationDetailForm />
      )}
    </div>
  );
};
